/**
 * Pure helpers shared between `useCompanyMemberships` and (in PR 3)
 * `LocationContext`. Kept here so they can be unit-tested without
 * pulling in the supabase client / react-query runtime.
 */

export interface CompanyMembership {
  id: string;
  user_id: string;
  company_id: string;
  role: 'super_admin' | 'admin' | 'corporate' | 'manager' | 'employee';
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * The primary membership, if any. Server orders by is_primary DESC so the
 * first row is the primary when one exists; this helper double-checks
 * defensively in case the client receives rows out of order.
 */
export function selectPrimary(memberships: CompanyMembership[]): CompanyMembership | null {
  return memberships.find((m) => m.is_primary) ?? null;
}

/**
 * The company id the rest of the app should treat as "current" for this
 * user.
 *
 * PR 6 (multi-tenancy completion): AuthContext now rewrites the exposed
 * `profile.company_id` to the ACTIVE company for multi-membership users,
 * so when the profile's company is one the user actually belongs to it is
 * authoritative — preferring the primary membership here would fight the
 * company switcher. For single-membership users (everyone today) the
 * profile company IS the sole membership's company, so behavior is
 * unchanged.
 *
 * Fallback order for drift (profile.company_id not among the memberships,
 * e.g. a not-yet-synced membership row): primary membership, then the raw
 * profile.company_id, so the caller is never left with `null` when a
 * company can be determined.
 */
export function resolveCompanyId(
  memberships: CompanyMembership[],
  profileCompanyId: string | null | undefined,
): string | null {
  if (profileCompanyId && memberships.some((m) => m.company_id === profileCompanyId)) {
    return profileCompanyId;
  }
  return selectPrimary(memberships)?.company_id ?? profileCompanyId ?? null;
}
